import { useEffect, useRef, useState } from 'react';
import { formatDateAU } from '../../../../shared/format';
import { addMonthsIso, currentValue, windowLabel, type EditTarget } from '../../../../shared/bookEdit';
import type { DashboardRow } from '../../../../shared/types';
import { useAnchoredPopover } from './useAnchoredPopover';

export type CellEdit =
  | { kind: 'value'; value: string | boolean | null }
  | { kind: 'note'; type: 'call' | 'meeting' | 'email' | 'text' | 'other'; content: string };

/**
 * In-grid editor for one cell of the book: record a compliance date, set or
 * clear a flag, add a quick file note, or change Call Due / Review Date. The
 * parent owns saving (optimistic update + undo); this resolves on success and
 * shows the error inline on failure.
 */
export function CellEditor({
  row, target, anchor, today, onSave, onClose,
}: {
  row: DashboardRow;
  target: EditTarget;
  anchor: HTMLElement;
  today: string;
  onSave: (edit: CellEdit) => Promise<void>;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pos = useAnchoredPopover(anchor, ref, onClose, 300);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cur = currentValue(row, target);
  const [date, setDate] = useState<string>(typeof cur === 'string' && target.kind === 'date' ? cur.slice(0, 10) : today);
  const [noteType, setNoteType] = useState<'call' | 'meeting' | 'email' | 'text' | 'other'>('call');
  const [note, setNote] = useState('');

  useEffect(() => { ref.current?.querySelector<HTMLElement>('textarea, button, input')?.focus(); }, []);

  async function save(edit: CellEdit) {
    setBusy(true);
    setError(null);
    try {
      await onSave(edit);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setBusy(false);
    }
  }

  const name = row.sortName ?? row.name;

  return (
    <div ref={ref} role="dialog" aria-label={`Edit ${target.label} for ${name}`} className="qa-book-pop qa-shadow" style={{ top: pos.top, left: pos.left, width: 300 }}>
      <p className="qa-eyebrow text-[10px] mb-1">{target.label}</p>
      <p className="text-sm font-medium text-ink mb-2 truncate">{name}</p>

      {target.kind === 'ruleDate' && (
        <>
          <p className="text-xs text-ink-mute mb-3">
            Last recorded: <span className="qa-mono text-ink">{cur ? formatDateAU(String(cur)) : 'never'}</span>
            <br />Current for {windowLabel(target.windowDays)} from that date.
          </p>
          <button type="button" className="btn-primary w-full text-sm mb-2" disabled={busy}
            onClick={() => save({ kind: 'value', value: today })}>
            Record today ({formatDateAU(today)})
          </button>
          <div className="flex gap-2 items-end">
            <label className="text-xs text-ink-mute flex-1">Or on
              <input type="date" className="qa-input mt-1 w-full text-sm" max={today} value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <button type="button" className="qa-book-pop__btn" disabled={busy || !date} onClick={() => save({ kind: 'value', value: date })}>Save</button>
          </div>
          {cur && (
            <button type="button" className="qa-book-pop__link mt-2" disabled={busy} onClick={() => save({ kind: 'value', value: null })}>
              Clear recorded date
            </button>
          )}
        </>
      )}

      {target.kind === 'flag' && (
        <>
          <p className="text-xs text-ink-mute mb-3">{cur ? 'Currently flagged as needed.' : 'Not currently needed.'}</p>
          <button type="button" className="btn-primary w-full text-sm" disabled={busy}
            onClick={() => save({ kind: 'value', value: !cur })}>
            {cur ? 'Mark done' : 'Flag as needed'}
          </button>
        </>
      )}

      {target.kind === 'note' && (
        <>
          <p className="text-xs text-ink-mute mb-2">
            Last note: <span className="qa-mono text-ink">{cur ? formatDateAU(String(cur)) : 'never'}</span>. Adding a note today makes this current.
          </p>
          <div className="flex gap-1 mb-2 flex-wrap">
            {(['call', 'meeting', 'email', 'text', 'other'] as const).map((t) => (
              <button key={t} type="button" aria-pressed={noteType === t}
                className={`qa-book-pop__btn capitalize ${noteType === t ? 'is-on' : ''}`} onClick={() => setNoteType(t)}>{t}</button>
            ))}
          </div>
          <textarea className="qa-input w-full text-sm" rows={3} aria-label="File note" placeholder="What was discussed?"
            value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className="btn-primary w-full text-sm mt-2" disabled={busy || !note.trim()}
            onClick={() => save({ kind: 'note', type: noteType, content: note.trim() })}>
            Add file note
          </button>
        </>
      )}

      {target.kind === 'date' && (
        <>
          <div className="grid grid-cols-4 gap-1 mb-2">
            {[1, 3, 6, 12].map((m) => (
              <button key={m} type="button" className="qa-book-pop__btn" disabled={busy}
                title={formatDateAU(addMonthsIso(today, m))}
                onClick={() => save({ kind: 'value', value: addMonthsIso(today, m) })}>
                +{m}m
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-end">
            <label className="text-xs text-ink-mute flex-1">Date
              <input type="date" className="qa-input mt-1 w-full text-sm" value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <button type="button" className="qa-book-pop__btn" disabled={busy || !date} onClick={() => save({ kind: 'value', value: date })}>Save</button>
          </div>
          {cur && (
            <button type="button" className="qa-book-pop__link mt-2" disabled={busy} onClick={() => save({ kind: 'value', value: null })}>
              Clear date
            </button>
          )}
        </>
      )}

      {error && <p role="alert" className="text-xs text-rust mt-2">{error}</p>}
      <div className="flex justify-end mt-3 pt-2 border-t border-hairline-paper">
        <button type="button" className="qa-book-pop__btn" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
